/**
 * HoleHexGrid — D3 SVG renderer for hex-format golf hole layouts.
 *
 * Replaces the canvas-based HoleVisualization component with a clean,
 * responsive SVG approach. Uses useChartDimensions for mobile support.
 *
 * SVG layer order (bottom → top):
 *   1. hex-terrain      — terrain fill for each hex
 *   2. hex-heatmap      — shot-density overlay at sub-hex resolution
 *   3. hex-start-heatmap — blue overlay showing where the selected shot started from
 *   4. waypoints        — numbered hex highlights + connecting polyline
 *   5. aim-lines        — dashed line from start position → aim target
 *   6. shot-paths       — replay: numbered circles + coloured lines
 *   7. markers          — pin flag and tee circle
 *   8. legend           — terrain colour key
 *   9. scale-bar        — distance reference bar
 *  10. tooltip          — hover info panel
 */

import React, { useMemo, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { useChartDimensions } from '../../../../components/graphs/useChartDimensions';
import { TERRAIN_COLOURS } from '../utility/holeSimulation';
import { heatColour, SHOT_COLOURS } from '../utility/canvasUtils';
import {
  hexBounds,
  hexCentreMetres,
  hexPointsString,
  metreToHex,
  buildHexMap,
} from '../utility/hexUtils';

// ── Constants ──────────────────────────────────────────────────────────────

const OOB_COLOUR = '#1a3a1a';
const WAYPOINT_COLOUR = '#ffffff';
const WAYPOINT_STROKE = '#444444';
const PIN_COLOUR = '#e53e3e';
const TEE_COLOUR = '#ffffff';

// Visual scale applied to rendered hex polygons only (not layout geometry).
// Keep at 1.0 for seamless tiling; values below 1.0 create visible gaps.
const HEX_RENDER_SCALE = 0.98;

// Number of sub-hex subdivisions for finer heatmap resolution.
// heatmapHexSize = hexSize / HEATMAP_SUBDIVISIONS
const HEATMAP_SUBDIVISIONS = 3;

// ── Scale helpers ──────────────────────────────────────────────────────────

/**
 * Build D3 linear scales mapping metre space → SVG pixel space.
 * Maintains the hole's aspect ratio within the available SVG width.
 */
function buildScales(bounds, svgWidth) {
  const aspectRatio = bounds.height / bounds.width;
  const svgHeight = Math.round(svgWidth * aspectRatio);
  const padding = { x: 4, y: 4 };

  const xScale = d3.scaleLinear()
    .domain([bounds.minX, bounds.maxX])
    .range([padding.x, svgWidth - padding.x]);

  const yScale = d3.scaleLinear()
    .domain([bounds.minY, bounds.maxY])
    .range([padding.y, svgHeight - padding.y]);

  // Pixel size: map hexSize metres to pixels (pixelsPerMetre × hexSizeMetres)
  const pixelSize = (svgWidth - 2 * padding.x) / bounds.width * bounds.hexSize;

  return { xScale, yScale, svgHeight, pixelSize };
}

// ── Heatmap binning ────────────────────────────────────────────────────────

/**
 * Bin landing positions into hexes at a given hexSize.
 * Returns a Map<"col,row", count> and the max count.
 */
function binLandingsToHexes(landings, hexSize) {
  const counts = new Map();
  for (const { x, y } of landings) {
    const { col, row } = metreToHex(x, y, hexSize);
    const key = `${col},${row}`;
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  let maxCount = 0;
  for (const c of counts.values()) {
    if (c > maxCount) maxCount = c;
  }
  return { counts, maxCount };
}

// ── Colour helpers ─────────────────────────────────────────────────────────

/** Blue-tinted palette for start-position heatmap. */
function startHeatColour(intensity) {
  const t = Math.min(1, intensity);
  if (t < 0.33) {
    return `rgba(100, 180, 255, ${(0.15 + t * 0.9).toFixed(2)})`;
  }
  if (t < 0.66) {
    const a = (t - 0.33) / 0.33;
    return `rgba(60, 100, 255, ${(0.45 + a * 0.25).toFixed(2)})`;
  }
  const a = (t - 0.66) / 0.34;
  return `rgba(${Math.round(60 - a * 20)}, ${Math.round(60 - a * 20)}, ${Math.round(220 + a * 35)}, ${(0.7 + a * 0.2).toFixed(2)})`;
}

// ── Sub-components ─────────────────────────────────────────────────────────

function TerrainLayer({ hexes, hexSize, xScale, yScale, pixelSize }) {
  return (
    <g className="hex-terrain">
      {hexes.map(({ col, row, terrain }) => {
        const { x: mx, y: my } = hexCentreMetres(col, row, hexSize);
        const px = xScale(mx);
        const py = yScale(my);
        const pts = hexPointsString(px, py, pixelSize * HEX_RENDER_SCALE);
        const fill = terrain === 'oob'
          ? OOB_COLOUR
          : (TERRAIN_COLOURS[terrain] || OOB_COLOUR);
        return (
          <polygon
            key={`t-${col}-${row}`}
            points={pts}
            fill={fill}
            stroke="none"
          />
        );
      })}
    </g>
  );
}

function HeatmapLayer({ hexes, hexSize, landings, xScale, yScale, pixelSize }) {
  const heatmapHexSize = hexSize / HEATMAP_SUBDIVISIONS;
  const heatmapPixelSize = (pixelSize / HEATMAP_SUBDIVISIONS) * HEX_RENDER_SCALE;

  const hexMap = useMemo(() => buildHexMap(hexes), [hexes]);

  const { counts, maxCount } = useMemo(
    () => binLandingsToHexes(landings, heatmapHexSize),
    [landings, heatmapHexSize]
  );

  if (!landings.length || maxCount === 0) return null;

  const polygons = [];
  for (const [key, count] of counts.entries()) {
    const [col, row] = key.split(',').map(Number);
    const { x: mx, y: my } = hexCentreMetres(col, row, heatmapHexSize);
    // Only render sub-hexes whose centre falls within a non-OOB parent hex
    const parentHex = metreToHex(mx, my, hexSize);
    const parentTerrain = hexMap.get(`${parentHex.col},${parentHex.row}`);
    if (!parentTerrain || parentTerrain === 'oob') continue;

    const intensity = Math.sqrt(count / maxCount);
    const fill = heatColour(intensity);
    const px = xScale(mx);
    const py = yScale(my);
    const pts = hexPointsString(px, py, heatmapPixelSize);
    polygons.push(
      <polygon key={`h-${col}-${row}`} points={pts} fill={fill} stroke="none" />
    );
  }

  return <g className="hex-heatmap">{polygons}</g>;
}

function StartHeatmapLayer({ hexes, hexSize, startPositions, xScale, yScale, pixelSize }) {
  const heatmapHexSize = hexSize / HEATMAP_SUBDIVISIONS;
  const heatmapPixelSize = (pixelSize / HEATMAP_SUBDIVISIONS) * HEX_RENDER_SCALE;

  const hexMap = useMemo(() => buildHexMap(hexes), [hexes]);

  const { counts, maxCount } = useMemo(
    () => binLandingsToHexes(startPositions, heatmapHexSize),
    [startPositions, heatmapHexSize]
  );

  if (!startPositions.length || maxCount === 0) return null;

  const polygons = [];
  for (const [key, count] of counts.entries()) {
    const [col, row] = key.split(',').map(Number);
    const { x: mx, y: my } = hexCentreMetres(col, row, heatmapHexSize);
    const parentHex = metreToHex(mx, my, hexSize);
    const parentTerrain = hexMap.get(`${parentHex.col},${parentHex.row}`);
    if (!parentTerrain || parentTerrain === 'oob') continue;

    const intensity = Math.sqrt(count / maxCount);
    const fill = startHeatColour(intensity);
    const px = xScale(mx);
    const py = yScale(my);
    const pts = hexPointsString(px, py, heatmapPixelSize);
    polygons.push(
      <polygon key={`sh-${col}-${row}`} points={pts} fill={fill} stroke="none" opacity={0.65} />
    );
  }

  return <g className="hex-start-heatmap">{polygons}</g>;
}

function WaypointsLayer({ waypoints, hexSize, xScale, yScale, pixelSize }) {
  if (!waypoints || waypoints.length === 0) return null;

  const centres = waypoints.map(([col, row]) => {
    const { x: mx, y: my } = hexCentreMetres(col, row, hexSize);
    return { px: xScale(mx), py: yScale(my), col, row };
  });

  const polylinePoints = centres.map(c => `${c.px},${c.py}`).join(' ');

  return (
    <g className="hex-waypoints">
      <polyline
        points={polylinePoints}
        fill="none"
        stroke={WAYPOINT_COLOUR}
        strokeWidth={1.5}
        strokeDasharray="4,3"
        opacity={0.7}
      />
      {centres.map(({ px, py, col, row }, i) => {
        const { x: mx, y: my } = hexCentreMetres(col, row, hexSize);
        const pts = hexPointsString(xScale(mx), yScale(my), pixelSize * HEX_RENDER_SCALE);
        const r = Math.max(pixelSize * 0.35, 5);
        return (
          <g key={`wp-${col}-${row}`}>
            <polygon
              points={pts}
              fill="none"
              stroke={WAYPOINT_STROKE}
              strokeWidth={1.5}
              opacity={0.8}
            />
            <circle cx={px} cy={py} r={r} fill={WAYPOINT_COLOUR} opacity={0.85} />
            <text
              x={px}
              y={py + 1}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={Math.max(r * 1.1, 7)}
              fontWeight="bold"
              fill="#222222"
            >
              {i + 1}
            </text>
          </g>
        );
      })}
    </g>
  );
}

function AimLinesLayer({ aimPoints, tee, xScale, yScale }) {
  if (!aimPoints || aimPoints.length === 0) return null;
  const defaultFrom = { x: tee.x, y: tee.y };

  return (
    <g className="hex-aim-lines">
      {aimPoints.map((pt, i) => {
        // Each point may be { from, to } (filtered mode) or { from: null, to: {x,y} } (all-shots mode)
        const fromCoord = pt.from ?? defaultFrom;
        const toCoord = pt.to ?? pt;
        return (
          <line
            key={`aim-${i}`}
            x1={xScale(fromCoord.x)} y1={yScale(fromCoord.y)}
            x2={xScale(toCoord.x)} y2={yScale(toCoord.y)}
            stroke="#ffffff"
            strokeWidth={1.5}
            strokeDasharray="5,4"
            opacity={0.7}
          />
        );
      })}
    </g>
  );
}

function ShotPathsLayer({ replayShots, xScale, yScale, pixelSize }) {
  if (!replayShots || replayShots.length === 0) return null;
  return (
    <g className="hex-shot-paths">
      {replayShots.map((shot, i) => {
        const colour = SHOT_COLOURS[i % SHOT_COLOURS.length];
        const x1 = xScale(shot.from.x);
        const y1 = yScale(shot.from.y);
        const x2 = xScale(shot.to.x);
        const y2 = yScale(shot.to.y);
        const r = Math.max(pixelSize * 0.4, 6);
        return (
          <g key={`shot-${i}`}>
            <line
              x1={x1} y1={y1} x2={x2} y2={y2}
              stroke={colour}
              strokeWidth={1.5}
              opacity={0.85}
            />
            <circle cx={x2} cy={y2} r={r} fill={colour} opacity={0.9} />
            <text
              x={x2} y={y2 + 1}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={Math.max(r * 0.95, 7)}
              fontWeight="bold"
              fill="#ffffff"
            >
              {i + 1}
            </text>
          </g>
        );
      })}
    </g>
  );
}

function MarkersLayer({ teeHex, pinHex, hexSize, xScale, yScale, pixelSize }) {
  const { x: tmx, y: tmy } = hexCentreMetres(teeHex[0], teeHex[1], hexSize);
  const { x: pmx, y: pmy } = hexCentreMetres(pinHex[0], pinHex[1], hexSize);
  const tpx = xScale(tmx);
  const tpy = yScale(tmy);
  const ppx = xScale(pmx);
  const ppy = yScale(pmy);
  const r = Math.max(pixelSize * 0.45, 5);
  const flagH = r * 2.5;
  const flagW = r * 1.8;

  return (
    <g className="hex-markers">
      {/* Tee marker */}
      <circle cx={tpx} cy={tpy} r={r} fill={TEE_COLOUR} stroke="#888888" strokeWidth={1} />
      <text
        x={tpx} y={tpy + 0.5}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={Math.max(r * 0.85, 6)}
        fill="#555555"
        fontWeight="bold"
      >
        T
      </text>
      {/* Pin flag */}
      <line
        x1={ppx} y1={ppy}
        x2={ppx} y2={ppy - flagH}
        stroke="#333333"
        strokeWidth={1.2}
      />
      <polygon
        points={`${ppx},${ppy - flagH} ${ppx + flagW},${ppy - flagH * 0.65} ${ppx},${ppy - flagH * 0.3}`}
        fill={PIN_COLOUR}
      />
      <circle cx={ppx} cy={ppy} r={r * 0.5} fill={PIN_COLOUR} />
    </g>
  );
}

function LegendLayer({ bounds, xScale, yScale, pixelSize }) {
  const terrainEntries = Object.entries(TERRAIN_COLOURS).filter(
    ([t]) => t !== 'oob'
  );
  const labels = {
    tee: 'Tee',
    fairway: 'Fairway',
    rough: 'Rough',
    bunker: 'Bunker',
    water: 'Water',
    green: 'Green',
  };

  const startX = xScale(bounds.minX) + 4;
  const startY = yScale(bounds.maxY) - 8;
  const itemH = Math.max(pixelSize * 1.0, 10);
  const swatchR = itemH * 0.35;
  const fontSize = Math.max(itemH * 0.55, 7);

  return (
    <g className="hex-legend">
      {terrainEntries.map(([terrain, colour], i) => {
        const y = startY - (terrainEntries.length - i) * (itemH + 2);
        return (
          <g key={`legend-${terrain}`}>
            <circle cx={startX + swatchR} cy={y} r={swatchR} fill={colour} />
            <text
              x={startX + swatchR * 2 + 3}
              y={y + 0.5}
              dominantBaseline="middle"
              fontSize={fontSize}
              fill="#cccccc"
            >
              {labels[terrain] || terrain}
            </text>
          </g>
        );
      })}
    </g>
  );
}

function ScaleBarLayer({ bounds, xScale, yScale, pixelSize, hexSize }) {
  // Bar represents 3 hex column steps = 3 × √3 × hexSize metres
  const barMetres = 3 * Math.sqrt(3) * hexSize;
  const barLabel = `~${Math.round(barMetres)}m`;

  const barEndX = xScale(bounds.maxX) - 8;
  const barStartX = barEndX - (xScale(bounds.maxX) - xScale(bounds.maxX - barMetres));
  const barY = yScale(bounds.maxY) - 16;
  const tickH = 5;
  const fontSize = Math.max(pixelSize * 0.5, 8);

  return (
    <g className="hex-scale-bar">
      <line x1={barStartX} y1={barY} x2={barEndX} y2={barY} stroke="#cccccc" strokeWidth={1.5} />
      <line x1={barStartX} y1={barY - tickH} x2={barStartX} y2={barY + tickH} stroke="#cccccc" strokeWidth={1.5} />
      <line x1={barEndX} y1={barY - tickH} x2={barEndX} y2={barY + tickH} stroke="#cccccc" strokeWidth={1.5} />
      <text
        x={(barStartX + barEndX) / 2}
        y={barY - tickH - 3}
        textAnchor="middle"
        fontSize={fontSize}
        fill="#cccccc"
      >
        {barLabel}
      </text>
    </g>
  );
}

function TooltipLayer({ tooltipState }) {
  if (!tooltipState) return null;
  const { px, py, terrain, distFromTee, landingCount } = tooltipState;

  const lines = [
    terrain && terrain !== 'oob' ? `Terrain: ${terrain.charAt(0).toUpperCase() + terrain.slice(1)}` : null,
    distFromTee != null ? `Dist. from tee: ${distFromTee.toFixed(0)}m` : null,
    landingCount != null && landingCount > 0 ? `Landings: ${landingCount}` : null,
  ].filter(Boolean);

  if (lines.length === 0) return null;

  const lineH = 13;
  const padX = 6;
  const padY = 5;
  const boxW = 138;
  const boxH = lines.length * lineH + padY * 2;
  // Offset box so it doesn't sit on the cursor; clamp to stay within reasonable bounds
  const boxX = px + 12;
  const boxY = py - 8;

  return (
    <g className="hex-tooltip" style={{ pointerEvents: 'none' }}>
      <rect
        x={boxX} y={boxY - boxH}
        width={boxW} height={boxH}
        fill="rgba(0,0,0,0.78)"
        rx={3}
      />
      {lines.map((line, i) => (
        <text
          key={i}
          x={boxX + padX}
          y={boxY - boxH + padY + i * lineH + lineH * 0.8}
          fontSize={10}
          fill="#ffffff"
        >
          {line}
        </text>
      ))}
    </g>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

/**
 * HoleHexGrid — responsive SVG renderer for hex-format hole layouts.
 *
 * @param {Object}  props.hole            - from createHoleLayoutFromHex
 * @param {Array}   props.landings        - [{ x, y }] metre coords from simulation
 * @param {boolean} props.showHeatmap
 * @param {Array}   props.aimPoints       - [{ from, to }] or [{ from: null, to: {x,y} }]
 * @param {Array}   props.startPositions  - [{ x, y }] start positions for selected shot
 * @param {Array}   props.replayShots     - [{ from, to }] metre coords for replay
 * @param {boolean} props.showLegend
 * @param {boolean} props.showWaypoints
 */
export function HoleHexGrid({
  hole,
  landings = [],
  showHeatmap = false,
  aimPoints = [],
  startPositions = [],
  replayShots = [],
  showLegend = true,
  showWaypoints = false,
}) {
  const [ref, dimensions] = useChartDimensions({
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
  });

  const [tooltipState, setTooltipState] = useState(null);

  const bounds = useMemo(() => {
    if (!hole?.hexData) return null;
    const b = hexBounds(hole.hexData.hexes, hole.hexData.hexSize);
    return { ...b, hexSize: hole.hexData.hexSize };
  }, [hole]);

  const scales = useMemo(() => {
    if (!bounds || dimensions.width <= 0) return null;
    return buildScales(bounds, dimensions.width);
  }, [bounds, dimensions.width]);

  // Convert replay shot shapes: expects { from: {x,y}, to: {x,y} }
  const replayShotPairs = useMemo(() => {
    if (!replayShots?.shots) return [];
    return replayShots.shots.map(s => ({ from: s.from, to: s.to }));
  }, [replayShots]);

  // Pre-build hexMap for tooltip lookups
  const hexMapForTooltip = useMemo(() => {
    if (!hole?.hexData) return null;
    return buildHexMap(hole.hexData.hexes);
  }, [hole]);

  // Pre-bin landing counts for tooltip
  const heatmapCounts = useMemo(() => {
    if (!landings.length || !hole?.hexData) return null;
    return binLandingsToHexes(landings, hole.hexData.hexSize).counts;
  }, [landings, hole]);

  const handleMouseMove = useCallback((e) => {
    if (!scales || !bounds || !hexMapForTooltip || !hole) return;
    const svgRect = e.currentTarget.getBoundingClientRect();
    const rawPx = e.clientX - svgRect.left;
    const rawPy = e.clientY - svgRect.top;
    const mx = scales.xScale.invert(rawPx);
    const my = scales.yScale.invert(rawPy);
    const hexSize = hole.hexData.hexSize;
    const { col, row } = metreToHex(mx, my, hexSize);
    const terrain = hexMapForTooltip.get(`${col},${row}`) || null;

    const tee = hole.tee;
    const { x: hcx, y: hcy } = hexCentreMetres(col, row, hexSize);
    const distFromTee = Math.sqrt((hcx - tee.x) ** 2 + (hcy - tee.y) ** 2);

    const landingCount = heatmapCounts?.get(`${col},${row}`) ?? null;

    setTooltipState({ px: rawPx, py: rawPy, terrain, distFromTee, landingCount });
  }, [scales, bounds, hexMapForTooltip, heatmapCounts, hole]);

  const handleMouseLeave = useCallback(() => setTooltipState(null), []);

  if (!hole?.hexData || !bounds || !scales) {
    return (
      <div ref={ref} className="w-full" style={{ minHeight: 200 }}>
        <div className="flex items-center justify-center h-32 text-gray-500 text-sm">
          Loading hole…
        </div>
      </div>
    );
  }

  const { hexes, hexSize } = hole.hexData;
  const { xScale, yScale, svgHeight, pixelSize } = scales;

  return (
    <div ref={ref} className="w-full">
      <svg
        width={dimensions.width}
        height={svgHeight}
        style={{ display: 'block', background: OOB_COLOUR }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <TerrainLayer
          hexes={hexes}
          hexSize={hexSize}
          xScale={xScale}
          yScale={yScale}
          pixelSize={pixelSize}
        />

        {showHeatmap && landings.length > 0 && (
          <HeatmapLayer
            hexes={hexes}
            hexSize={hexSize}
            landings={landings}
            xScale={xScale}
            yScale={yScale}
            pixelSize={pixelSize}
          />
        )}

        {showHeatmap && startPositions.length > 0 && (
          <StartHeatmapLayer
            hexes={hexes}
            hexSize={hexSize}
            startPositions={startPositions}
            xScale={xScale}
            yScale={yScale}
            pixelSize={pixelSize}
          />
        )}

        {showWaypoints && hole.hexData.waypoints && (
          <WaypointsLayer
            waypoints={hole.hexData.waypoints}
            hexSize={hexSize}
            xScale={xScale}
            yScale={yScale}
            pixelSize={pixelSize}
          />
        )}

        {aimPoints.length > 0 && (
          <AimLinesLayer
            aimPoints={aimPoints}
            tee={hole.tee}
            xScale={xScale}
            yScale={yScale}
          />
        )}

        {replayShotPairs.length > 0 && (
          <ShotPathsLayer
            replayShots={replayShotPairs}
            xScale={xScale}
            yScale={yScale}
            pixelSize={pixelSize}
          />
        )}

        <MarkersLayer
          teeHex={hole.teeHex}
          pinHex={hole.pinHex}
          hexSize={hexSize}
          xScale={xScale}
          yScale={yScale}
          pixelSize={pixelSize}
        />

        {showLegend && (
          <LegendLayer
            bounds={bounds}
            xScale={xScale}
            yScale={yScale}
            pixelSize={pixelSize}
          />
        )}

        <ScaleBarLayer
          bounds={bounds}
          xScale={xScale}
          yScale={yScale}
          pixelSize={pixelSize}
          hexSize={hexSize}
        />

        <TooltipLayer tooltipState={tooltipState} />
      </svg>
    </div>
  );
}
